/**
 * RememberDevice.js
 */

import { ref } from 'vue';
import EnhancedSecurity from './EnhancedSecurity';
import SessionManager from './SessionManager';
import SessionValidator from './SessionValidator';

class RememberDevice {
  constructor() {
    this.cookieName = 'remember_device';
    this.expiryDays = 30;
    this.secureFlag = window.location.protocol === 'https:';
    this.encryptionKey = this.deriveAppSecret();
    this.fingerprintCache = ref(null);
  }

  deriveAppSecret() {
    // Use a fixed salt instead of domain to ensure consistency across environments
    const salt = 'remember_device_v2_salt';
    const baseKey = `${salt}|${navigator.userAgent.substring(0, 20)}`;
    return EnhancedSecurity.hashData(baseKey);
  }

  setCookie(name, value, days) {
    try {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      const expires = `; expires=${date.toUTCString()}`;
      const sameSite = '; SameSite=Strict'; 
      const secure = this.secureFlag ? '; Secure' : '';
      
      
      // Don't set domain for localhost or IP addresses
      let domain = '';
      const hostname = window.location.hostname;
      if (hostname !== 'localhost' && 
          !hostname.match(/^[0-9.]+$/) && 
          hostname.indexOf('.') !== -1) {
        // For subdomains, use the main domain
        // For example, sub.example.com -> .example.com
        try {
          const parts = hostname.split('.');
          if (parts.length >= 2) {
            // If it's a known TLD with country code like .co.uk, use last 3 parts
            if ((parts.length > 2) && 
                ((parts[parts.length-2] === 'co') || 
                 (parts[parts.length-2] === 'com') || 
                 (parts[parts.length-2] === 'org'))) {
              domain = `; domain=.${parts.slice(-3).join('.')}`;
            } else {
              domain = `; domain=.${parts.slice(-2).join('.')}`;
            }
          }
        } catch (domainErr) {
          console.log('RememberDevice: Error setting domain', domainErr);
          // If there's an error, don't set domain
          domain = '';
        }
      }
      
      console.log('RememberDevice: Setting cookie', { 
        name, 
        expires: date.toUTCString(),
        domain: domain || 'none',
        secure: this.secureFlag
      });
      
      document.cookie = `${name}=${value}${expires}; path=/${domain}${sameSite}${secure}`;
      
      // Verify cookie was set
      setTimeout(() => {
        const cookieExists = document.cookie.split(';').some(c => {
          return c.trim().startsWith(name + '=');
        });
        console.log('RememberDevice: Cookie verification', { exists: cookieExists });
      }, 100);
    } catch (e) {
      console.log('RememberDevice: Error setting cookie', e);
    }
  }

  getCookie(name) {
    try {
      const nameEQ = `${name}=`;
      const cookies = document.cookie.split(';');
      
      for (let i = 0; i < cookies.length; i++) {
        let cookie = cookies[i];
        while (cookie.charAt(0) === ' ') {
          cookie = cookie.substring(1);
        }
        
        if (cookie.indexOf(nameEQ) === 0) {
          return cookie.substring(nameEQ.length);
        }
      }
      
      return null;
    } catch (e) {
      return null;
    }
  }

  deleteCookie(name) {
    this.setCookie(name, '', -1);
  }

  generateDeviceId() {
    return new Promise((resolve) => {
      this.getDeviceFingerprint().then(fingerprint => {
        const timestamp = Date.now().toString(36);
        const randomStr = Math.random().toString(36).substring(2, 10);
        const combinedData = `${fingerprint}|${timestamp}|${randomStr}`;
        resolve(EnhancedSecurity.hashData(combinedData));
      });
    });
  }
  getDeviceFingerprint() {
    return new Promise((resolve) => {
      if (this.fingerprintCache.value) {
        resolve(this.fingerprintCache.value);
        return;
      }
      
      // First try to get the fingerprint from localStorage for consistency
      const storageKey = 'device_fingerprint_v2';
      const storedFingerprint = localStorage.getItem(storageKey);
      
      if (storedFingerprint) {
        this.fingerprintCache.value = storedFingerprint;
        resolve(storedFingerprint);
        return;
      }
      
      // If no stored fingerprint, generate a new one
      try {
        // Try the FingerprintJS approach first
        import('@fingerprintjs/fingerprintjs')
          .then(FingerprintJS => FingerprintJS.load())
          .then(fp => fp.get())
          .then(result => {
            const fingerprint = result.visitorId;
            // Store for future use
            localStorage.setItem(storageKey, fingerprint);
            this.fingerprintCache.value = fingerprint;
            resolve(fingerprint);
          })
          .catch(() => {
            // Fallback to basic fingerprint
            const domain = window.location.hostname;
            const userAgent = navigator.userAgent.substring(0, 50);
            const language = navigator.language || '';
            const screenInfo = `${window.screen.width}x${window.screen.height}`;
            const colorDepth = window.screen.colorDepth || '';
            const platform = navigator.platform || '';
            const randomSeed = Math.random().toString(36).substring(2, 10);
            
            // Create a stable fingerprint from these values plus a random seed
            // The random seed ensures uniqueness but will be consistent because we store it
            const stableFingerprint = EnhancedSecurity.hashData(
              `${domain}|${userAgent}|${language}|${screenInfo}|${colorDepth}|${platform}|${randomSeed}`
            );
            
            // Store for future use
            localStorage.setItem(storageKey, stableFingerprint);
            this.fingerprintCache.value = stableFingerprint;
            resolve(stableFingerprint);
          });
      } catch (error) {
        // Ultimate fallback - generate a random fingerprint and store it
        const fallbackFingerprint = Date.now().toString(36) + Math.random().toString(36).substring(2, 10);
        localStorage.setItem(storageKey, fallbackFingerprint);
        this.fingerprintCache.value = fallbackFingerprint;
        resolve(fallbackFingerprint);
      }
    });
  }
  
  getBrowserName() {
    const userAgent = navigator.userAgent;
    let browserName = "Unknown";
    
    if (userAgent.match(/chrome|chromium|crios/i)) {
      browserName = "Chrome";
    } else if (userAgent.match(/firefox|fxios/i)) {
      browserName = "Firefox";
    } else if (userAgent.match(/safari/i)) {
      browserName = "Safari";
    } else if (userAgent.match(/opr\//i)) {
      browserName = "Opera";
    } else if (userAgent.match(/edg/i)) {
      browserName = "Edge";
    } else if (userAgent.match(/msie|trident/i)) {
      browserName = "IE";
    }
    
    return browserName;
  }

  generateBasicFingerprint() {
    return EnhancedSecurity.generateBasicFingerprint();
  }

  hashString(str) {
    return EnhancedSecurity.simpleHash(str);
  }

  isDeviceRemembered() {
    console.log('RememberDevice: Checking if device is remembered');
    return new Promise((resolve) => {
      try {
        // First try to get from cookie
        let deviceCookie = this.getCookie(this.cookieName);
        console.log('RememberDevice: Cookie found?', !!deviceCookie);
        
        // If no cookie, try localStorage backup
        if (!deviceCookie) {
          console.log('RememberDevice: Trying localStorage backup');
          deviceCookie = localStorage.getItem('remember_device_backup');
          
          // If found in localStorage, restore the cookie
          if (deviceCookie) {
            console.log('RememberDevice: Restoring cookie from backup');
            this.setCookie(this.cookieName, deviceCookie, this.expiryDays);
          }
        }
        
        if (!deviceCookie) {
          console.log('RememberDevice: No device cookie found');
          try {
            const sessionId = SessionManager.getSessionId();
            const userId = SessionManager.getUserId();
            console.log('RememberDevice: Session check', { sessionId: !!sessionId, userId: !!userId });
            
            if (sessionId && userId) {
              SessionValidator.validateCurrentSession().then(isValid => {
                console.log('RememberDevice: Session validation result', isValid);
                if (isValid) {
                  resolve({ userId, timestamp: Date.now() });
                } else {
                  resolve(null);
                }
              }).catch((err) => {
                console.log('RememberDevice: Session validation error', err);
                resolve(null);
              });
            } else {
              resolve(null);
            }
          } catch (e) {
            console.log('RememberDevice: Error checking session', e);
            resolve(null);
          }
          return;
        }
        
        try {
          console.log('RememberDevice: Decrypting cookie data');
          const decryptedData = EnhancedSecurity.decryptData(deviceCookie, this.encryptionKey);
          
          if (!decryptedData) {
            console.log('RememberDevice: Decryption failed');
            this.deleteCookie(this.cookieName);
            localStorage.removeItem('remember_device_backup');
            resolve(null);
            return;
          }
          
          console.log('RememberDevice: Parsing device data');
          const deviceData = JSON.parse(decryptedData);
          console.log('RememberDevice: Device data', { 
            hasUserId: !!deviceData.userId, 
            hasEmail: !!deviceData.email 
          });
          
          if (!deviceData.userId || !deviceData.email) {
            console.log('RememberDevice: Missing required data');
            resolve(null);
            return;
          }
          
          this.getDeviceFingerprint().then(currentFingerprint => {
            console.log('RememberDevice: Fingerprint comparison', { 
              stored: deviceData.fingerprint?.substring(0, 8),
              current: currentFingerprint?.substring(0, 8),
              match: deviceData.fingerprint === currentFingerprint
            });
            
            // For backward compatibility, update the fingerprint if it doesn't match
            // instead of deleting the cookie
            if (deviceData.fingerprint !== currentFingerprint) {
              console.log('RememberDevice: Updating fingerprint');
              deviceData.fingerprint = currentFingerprint;
              deviceData.timestamp = Date.now();
              
              const encryptedData = EnhancedSecurity.encryptData(
                JSON.stringify(deviceData),
                this.encryptionKey
              );
              
              this.setCookie(this.cookieName, encryptedData, this.expiryDays);
              localStorage.setItem('remember_device_backup', encryptedData);
            }
            
            const now = Date.now();
            const cookieAge = now - deviceData.timestamp;
            const maxAge = this.expiryDays * 24 * 60 * 60 * 1000;
            
            console.log('RememberDevice: Cookie age check', { 
              age: Math.round(cookieAge / (24 * 60 * 60 * 1000)) + ' days',
              maxAge: this.expiryDays + ' days',
              expired: cookieAge > maxAge
            });
            
            if (cookieAge > maxAge) {
              console.log('RememberDevice: Cookie expired');
              this.deleteCookie(this.cookieName);
              localStorage.removeItem('remember_device_backup');
              resolve(null);
              return;
            }
            
            try {
              const sessionId = SessionManager.getSessionId();
              console.log('RememberDevice: Session check', { hasSession: !!sessionId });
              
              if (!sessionId && deviceData.userId && deviceData.email) {
                console.log('RememberDevice: Creating new session');
                SessionManager.createSession(deviceData.userId, deviceData.email).then(() => {
                  SessionValidator.startValidation();
                  console.log('RememberDevice: Session created successfully');
                  resolve(deviceData);
                }).catch((err) => {
                  console.log('RememberDevice: Session creation failed, but continuing', err);
                  resolve(deviceData);
                });
              } else {
                console.log('RememberDevice: Using existing session or no session needed');
                resolve(deviceData);
              }
            } catch (e) {
              console.log('RememberDevice: Error checking session', e);
              resolve(deviceData);
            }
          }).catch((err) => {
            console.log('RememberDevice: Error getting fingerprint', err);
            resolve(null);
          });
        } catch (e) {
          console.log('RememberDevice: Error processing cookie data', e);
          this.deleteCookie(this.cookieName);
          localStorage.removeItem('remember_device_backup');
          resolve(null);
        }
      } catch (e) {
        console.log('RememberDevice: Unexpected error', e);
        resolve(null);
      }
    });
  }

  rememberDevice(userId, email) {
    console.log('RememberDevice: Attempting to remember device', { userId, email });
    return new Promise((resolve, reject) => {
      Promise.all([
        this.generateDeviceId(),
        this.getDeviceFingerprint()
      ]).then(([deviceId, fingerprint]) => {
        console.log('RememberDevice: Got fingerprint and device ID');
        const browserName = this.getBrowserName();
        
        const deviceData = {
          deviceId,
          userId,
          email,
          fingerprint,
          browser: browserName,
          timestamp: Date.now(),
          created: Date.now()
        };
        
        try {
          console.log('RememberDevice: Encrypting device data');
          const encryptedData = EnhancedSecurity.encryptData(
            JSON.stringify(deviceData), 
            this.encryptionKey
          );
          
          console.log('RememberDevice: Setting cookie');
          this.setCookie(this.cookieName, encryptedData, this.expiryDays);
          
          // Also store in localStorage as backup
          try {
            localStorage.setItem('remember_device_backup', encryptedData);
          } catch (storageErr) {
            console.log('RememberDevice: Could not store backup in localStorage');
          }
          
          console.log('RememberDevice: Creating session');
          SessionManager.createSession(userId, email).then((sessionCreated) => {
            if (!sessionCreated) {
              throw new Error('Remember-device session could not be created');
            }
            SessionValidator.startValidation();
            console.log('RememberDevice: Device remembered successfully');
            resolve();
          }).catch((err) => {
            console.error('RememberDevice: Session creation failed', err);
            reject(err);
          });
        } catch (e) {
          console.log('RememberDevice: Error remembering device', e);
          reject(e);
        }
      }).catch(error => {
        console.log('RememberDevice: Error getting fingerprint', error);
        reject(error);
      });
    });
  }

  /**
   * Extend the device cookie expiration by another 30 days
   * @returns {Promise<void>}
   */
  extendRememberedDevice() {
    return new Promise((resolve, reject) => {
      this.isDeviceRemembered().then(deviceData => {
        if (deviceData) {
          deviceData.timestamp = Date.now();
          
          Promise.all([
            this.getDeviceFingerprint()
          ]).then(([fingerprint]) => {
            deviceData.fingerprint = fingerprint;
            deviceData.browser = this.getBrowserName();
            
            const encryptedData = EnhancedSecurity.encryptData(
              JSON.stringify(deviceData), 
              this.encryptionKey
            );
            
            this.setCookie(this.cookieName, encryptedData, this.expiryDays);
            
            const sessionId = SessionManager.getSessionId();
            const userId = SessionManager.getUserId();
            
            if (sessionId && userId && deviceData.userId === userId) {
              // Validate the session
              SessionValidator.validateCurrentSession().then(isValid => {
                if (!isValid && deviceData.userId && deviceData.email) {
                  // Create a new session if the current one is invalid
                  SessionManager.createSession(deviceData.userId, deviceData.email).then(() => {
                    SessionValidator.startValidation();
                    resolve();
                  }).catch(() => {
                    resolve();
                  });
                } else {
                  resolve();
                }
              }).catch(() => {
                resolve();
              });
            } else if (deviceData.userId && deviceData.email) {
              // Create a new session if none exists
              SessionManager.createSession(deviceData.userId, deviceData.email).then(() => {
                SessionValidator.startValidation();
                resolve();
              }).catch(() => {
                resolve();
              });
            } else {
              resolve();
            }
          }).catch(error => {
            reject(error);
          });
        } else {
          resolve();
        }
      }).catch(error => {
        reject(error);
      });
    });
  }

  /**
   * Get client IP address using a public API
   * @returns {Promise<string>} Promise resolving to IP address or 'unknown'
   */
  getClientIP() {
    return new Promise((resolve) => {
      try {
        // Use a public API to get the client's IP address
        fetch('https://devhub.rezcomm.com/hubapmydbauth/brandapi/ip.php')
          .then(response => response.json())
          .then(data => {
            resolve(data.ip || 'unknown'); 
          })
          .catch(() => {
            resolve('unknown');
          });
      } catch (error) {
        resolve('unknown');
      }
    });
  } 

  /**
   * Forget the current device
   */
  forgetDevice() {
    // Delete the cookie
    this.deleteCookie(this.cookieName);
    this.fingerprintCache.value = null;
    
    // Also invalidate the secure session
    SessionManager.invalidateSession();
    SessionValidator.stopValidation();
  }
}

export default new RememberDevice();
